import React, { ReactElement, useEffect, useState } from "react";

import clsx from "clsx";
import { ConnectedProps, connect, useDispatch } from "react-redux";

import { Grid, Theme } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { DataGrid, GridCellParams, GridColDef, GridRenderCellParams, GridSelectionModel } from "@mui/x-data-grid";

import { nonce } from "App/App";
import { AppDispatch, RootState } from "App/Store";

import * as constants from "Common/EVaultAppConstants";

import ErrorOverlay from "Components/DataGridCustomComponents/ErrorOverlay";
import LoadingOverlay from "Components/DataGridCustomComponents/LoadingOverlay";
import TableMenuItem from "Components/Table/TableMenuItem";
import TableSelect from "Components/Table/TableSelect";

import {
  IVaultViewClosedLoansSearchResultData,
  MersModificationTypes,
  VaultViewClosedLoansTabTypes,
} from "Features/Vault/VaultInterfaces";
import { datagridColumns } from "Features/Vault/shared";

import MersIdentificationNumber from "../MersIdentificationNumber";
import { IMersUpdateProps } from "./MersUpdate";
import { updateChangeType } from "./MersUpdateSlice";
import { fetchUpdates, updateUpdateQueue } from "./mersUpdateService";

const mapStateToProps = (state: RootState) => ({
  error: state.mersUpdates.error,
  initialized: state.mersUpdates.initialized,
  isLoading: state.mersUpdates.isLoading,
});

const connector = connect(mapStateToProps);
type TypesFromRedux = ConnectedProps<typeof connector>;

type Props = IMersUpdateProps & TypesFromRedux;

export function UpdateModificationTable(props: Props): ReactElement {
  const keyRoot = "VaultTab-ClosedLoans-";
  const dispatch = useDispatch() as AppDispatch;
  const classes = useStyles();
  const { error, initialized, isLoading, queueList, updateSet } = props;

  const [selected, setSelected] = useState<GridSelectionModel>(
    queueList.filter((loan) => loan.selected).map((loan) => loan.packageId)
  );

  useEffect(() => {
    if (!initialized) {
      dispatch(fetchUpdates());
    }
  }, [dispatch, initialized]);

  const columns: GridColDef[] = [
    {
      ...datagridColumns.loanNumber,
      sortable: false,
    },
    {
      ...datagridColumns.min,
      renderCell: (params: GridRenderCellParams) => (
        <MersIdentificationNumber
          keyRoot={keyRoot}
          loan={params.row as IVaultViewClosedLoansSearchResultData}
          loanType={constants.CLOSED_LOANS}
          tabKeyRoot={VaultViewClosedLoansTabTypes.MERSInquiryDetails}
          value={params.row.min}
        />
      ),
      sortable: false,
    },
    datagridColumns.primaryBorrowerName,
    {
      cellClassName: (params: GridCellParams) =>
        clsx({
          invalid:
            params.row.selected &&
            (params.row.changeData == null || params.row.changeData === MersModificationTypes.None.toString()),
        }),
      field: "updateTypeSelector",
      flex: 1,
      headerName: updateSet.toUpperCase(),
      renderCell: (params: GridRenderCellParams) => {
        const changeDataOptions = [
          <TableMenuItem key={MersModificationTypes.None} value={MersModificationTypes.None.toString()}>
            none
          </TableMenuItem>,
          <TableMenuItem key={MersModificationTypes.Paper} value={MersModificationTypes.Paper}>
            Paper Modification
          </TableMenuItem>,
        ];

        return (
          <Grid container item alignItems="center" justifyContent="center" sx={{ px: 1 }}>
            <TableSelect
              fullWidth
              displayEmpty={true}
              onChange={(e) => handleENoteUpdateChange(e, params.row)}
              size="small"
              value={params.row.changeData || MersModificationTypes.None.toString()}
              name="changeData"
              variant="standard"
            >
              {changeDataOptions}
            </TableSelect>
          </Grid>
        );
      },
      sortable: false,
    },
  ];

  function handleENoteUpdateChange(e: any, loan: any) {
    dispatch(
      updateChangeType({
        packageId: loan.packageId,
        property: e.target.name,
        value: e.target.value,
      })
    );
  }

  const onSelectionModelChange = (selected: GridSelectionModel) => {
    setSelected(selected);
    const updates = queueList.map((queue) => {
      return {
        ...queue,
        selected: selected.includes(queue.packageId),
      };
    });
    dispatch(updateUpdateQueue(updates));
  };

  return (
    <DataGrid
      autoHeight
      checkboxSelection
      classes={{
        root: classes.root,
      }}
      columnBuffer={0}
      columns={columns}
      components={{
        ErrorOverlay: ErrorOverlay,
        LoadingOverlay: LoadingOverlay,
      }}
      componentsProps={{
        errorOverlay: { error },
      }}
      disableColumnFilter
      disableColumnMenu
      disableColumnSelector
      disableSelectionOnClick
      editMode="row"
      error={error}
      hideFooter
      loading={isLoading}
      localeText={{
        noRowsLabel: "No results found",
      }}
      nonce={nonce}
      onSelectionModelChange={onSelectionModelChange}
      selectionModel={selected}
      rows={queueList}
      scrollbarSize={0}
    />
  );
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    "& .invalid": {
      background: `${theme.palette.error.main}20`,
    },
  },
}));

export default connector(UpdateModificationTable);
